// src/app/components/mfalogin/mfalogin.ts

import { Component, OnInit, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from '../../services/auth';
import { RoleAccess } from '../../models/user.model';

@Component({
  selector: 'app-mfalogin',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './mfalogin.html',
  styleUrl: './mfalogin.scss'
})
export class MFALogin implements OnInit, AfterViewInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  mfaForm: FormGroup;
  isLoading = false;
  isRoleConfirming = false;
  
  // Individual OTP digit controls
  otp1 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);
  otp2 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);
  otp3 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);
  otp4 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);
  otp5 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);
  otp6 = new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/)]);

  // Role selection state
  showRoleSelection = false;
  availableRoles: RoleAccess[] = [];
  selectedRole: RoleAccess | null = null;
  
  // ✅ NEW: Prevent duplicate submissions
  private isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.mfaForm = this.fb.group({
      otp1: this.otp1,
      otp2: this.otp2,
      otp3: this.otp3,
      otp4: this.otp4,
      otp5: this.otp5,
      otp6: this.otp6
    });
  }

  ngOnInit(): void {
    // Check if we have login session
    const session = this.authService.getLoginSession();
    if (!session) {
      this.snackBar.open('Session expired. Please login again.', '', { duration: 3000 });
      this.router.navigate(['/login']);
    }
  }

  ngAfterViewInit(): void {
    // Focus first input on load
    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      if (inputs.length > 0) {
        inputs[0].nativeElement.focus();
      }
    }, 100);
  }

  /**
   * ✅ FIXED: Handle OTP input with proper validation
   */
  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // ✅ Handle multiple digits or non-numeric input
    value = value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length > 1) {
      // Handle paste or multiple digit entry
      value = value.charAt(0);
    }
    
    input.value = value;

    // Update form control value explicitly
    const controls = [this.otp1, this.otp2, this.otp3, this.otp4, this.otp5, this.otp6];
    controls[index].setValue(value);

    // Auto-focus next input
    if (value && index < 5) {
      const inputs = this.otpInputs.toArray();
      inputs[index + 1].nativeElement.focus();
    }

    // ✅ FIXED: Auto-submit with proper validation check
    if (index === 5 && value) {
      // Use requestAnimationFrame to ensure form state is updated
      requestAnimationFrame(() => {
        const allFilled = this.isOtpComplete();
        if (allFilled && this.mfaForm.valid && !this.isSubmitting) {
          this.onSubmit();
        }
      });
    }
  }

  /**
   * ✅ NEW: Check if all OTP fields are complete
   */
  private isOtpComplete(): boolean {
    return !!(
      this.otp1.value && 
      this.otp2.value && 
      this.otp3.value && 
      this.otp4.value && 
      this.otp5.value && 
      this.otp6.value
    );
  }

  /**
   * ✅ FIXED: Handle backspace properly
   */
  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        // Move to previous input if current is empty
        const inputs = this.otpInputs.toArray();
        inputs[index - 1].nativeElement.focus();
      }
      // Clear current value
      const controls = [this.otp1, this.otp2, this.otp3, this.otp4, this.otp5, this.otp6];
      controls[index].setValue('');
    }
  }

  /**
   * ✅ FIXED: Handle paste with proper validation
   */
  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length === 6) {
      this.otp1.setValue(digits[0]);
      this.otp2.setValue(digits[1]);
      this.otp3.setValue(digits[2]);
      this.otp4.setValue(digits[3]);
      this.otp5.setValue(digits[4]);
      this.otp6.setValue(digits[5]);

      // Mark all as touched for validation
      this.mfaForm.markAllAsTouched();

      // Focus last input
      const inputs = this.otpInputs.toArray();
      inputs[5].nativeElement.focus();

      // ✅ Auto-submit after paste
      requestAnimationFrame(() => {
        if (this.mfaForm.valid && !this.isSubmitting) {
          this.onSubmit();
        }
      });
    }
  }

  /**
   * ✅ FIXED: Submit MFA code with proper state management
   */
  onSubmit(): void {
    // ✅ Prevent duplicate submissions
    if (this.isSubmitting || this.isLoading) {
      return;
    }

    // ✅ Validate form explicitly
    if (this.mfaForm.invalid || !this.isOtpComplete()) {
      this.snackBar.open('Please enter all 6 digits', '', { duration: 2000 });
      return;
    }

    // Combine OTP digits
    const totp = `${this.otp1.value}${this.otp2.value}${this.otp3.value}${this.otp4.value}${this.otp5.value}${this.otp6.value}`;
    this.isLoading = true;
    this.isSubmitting = true;

    this.authService.verifyMFA(totp).subscribe({
      next: (response) => {
        if (response.status === 200) {

          const contentScope = response.data.roleAccessList[0]?.contentScope;
          sessionStorage.setItem('contentScope', contentScope);

          // Get available roles
          this.availableRoles = this.authService.getAvailableRoles();

          // Check if single or multiple roles
          if (this.availableRoles.length === 1) {
            // ✅ FIXED: Single role - auto confirm and navigate
            this.selectedRole = this.availableRoles[0];
            this.confirmRoleSelection();
          } else if (this.availableRoles.length > 1) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.showRoleSelection = true;
          } else {
            // No roles - error
            this.isLoading = false;
            this.isSubmitting = false;
            this.snackBar.open('No roles assigned to this account', '', { duration: 4000 });
          }
        } else {
          this.isLoading = false;
          this.isSubmitting = false;
          this.snackBar.open(response.message || 'Verification failed', '', { duration: 4000 });
          this.clearOtp();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.isSubmitting = false;

        let errorMessage = 'Verification failed. Please try again.';
        
        if (error.status === 401) {
          errorMessage = 'Invalid verification code';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.snackBar.open(errorMessage, '', { duration: 4000 });
        
        // Clear OTP fields
        this.clearOtp();
      }
    });
  }

  /**
   * Handle role selection
   */
  onRoleSelect(role: RoleAccess): void {
    if (!this.isRoleConfirming) {
      this.selectedRole = role;
    }
  }

  /**
   * ✅ FIXED: Confirm role selection with proper navigation
   */
confirmRoleSelection(): void {
  if (!this.selectedRole || this.isRoleConfirming) {
    if (!this.selectedRole) {
      this.snackBar.open('Please select a role to continue', '', { duration: 2000 });
    }
    return;
  }

  // ✅ Show loading state
  this.isRoleConfirming = true;
  this.isLoading = true;

  try {
    // ✅ Complete authentication first
    this.authService.selectRoleAndAuthenticate(this.selectedRole);
    
    // ✅ FIXED: Wait for navigation to complete
    const dashboardRoute = this.authService.getDashboardRoute(
      this.authService.getUserRole()!
    );
    
    // ✅ Navigate with error handling
    this.router.navigate([dashboardRoute]).then(
      (success) => {
        if (success) {
          this.snackBar.open('Login successful!', '', { duration: 2000 });
          this.isRoleConfirming = false;
          this.isLoading = false;
        } else {
          this.isRoleConfirming = false;
          this.isLoading = false;
          this.snackBar.open('Navigation failed. Please try again.', '', { duration: 3000 });
        }
      },
      (error) => {
        this.isRoleConfirming = false;
        this.isLoading = false;
        this.snackBar.open('Failed to load dashboard. Please try again.', '', { duration: 3000 });
      }
    );
    
  } catch (error) {
    this.isRoleConfirming = false;
    this.isLoading = false;
    this.snackBar.open('Failed to complete login', '', { duration: 3000 });
  }
}


  /**
   * Get display name for role
   */
  getRoleDisplayName(bundleName: string): string {
    const roleMap: { [key: string]: string } = {
      'authoring_bundle': 'Author',
      'admin_bundle': 'Admin',
      'teacher_bundle': 'Teacher'
    };
    return roleMap[bundleName] || bundleName;
  }

  /**
   * Get icon for role
   */
  getRoleIcon(bundleName: string): string {
    const iconMap: { [key: string]: string } = {
      'authoring_bundle': 'edit',
      'teacher_bundle': 'school',
      'admin_bundle': 'admin_panel_settings'
    };
    return iconMap[bundleName] || 'person';
  }

  /**
   * Get description for role
   */
  getRoleDescription(bundleName: string): string {
    const descMap: { [key: string]: string } = {
      'authoring_bundle': 'Create and manage course content',
      'admin_bundle': 'Full system administration access',
      'teacher_bundle': 'Teaching and course management'
    };
    return descMap[bundleName] || '';
  }

  /**
   * ✅ FIXED: Clear all OTP fields with proper reset
   */
  clearOtp(): void {
    this.otp1.setValue('');
    this.otp2.setValue('');
    this.otp3.setValue('');
    this.otp4.setValue('');
    this.otp5.setValue('');
    this.otp6.setValue('');
    
    // Reset touched state
    this.mfaForm.markAsUntouched();
    this.mfaForm.markAsPristine();
    
    // Focus first input
    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      if (inputs.length > 0) {
        inputs[0].nativeElement.focus();
      }
    }, 100);
  }
}
